import React, { useCallback, useEffect, useState } from 'react';
import { TicketsService } from '../services/ticketsService';
import {
  TICKET_STATUSES,
  TICKET_STATUS_LABELS,
  TICKET_STATUS_TRANSITIONS,
  type Ticket,
  type TicketStatus,
} from '../types/ticket';
import TicketCard from './TicketCard';

const selectClass =
  'px-3 py-2 bg-[#334155] border border-[#475569] rounded-lg text-white text-sm focus:outline-none focus:border-[#1f9e3f] cursor-pointer';

const AdminTicketRow: React.FC<{
  ticket: Ticket;
  onChanged: () => void;
}> = ({ ticket, onChanged }) => {
  const options = TICKET_STATUS_TRANSITIONS[ticket.status];
  const [next, setNext] = useState<TicketStatus | ''>('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsNotes = next === 'resolved' || next === 'closed';

  const apply = async () => {
    if (!next) return;
    setBusy(true);
    setError(null);
    try {
      await TicketsService.updateStatus(
        ticket.id,
        next,
        needsNotes && notes ? notes : undefined,
      );
      setNext('');
      setNotes('');
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <TicketCard ticket={ticket}>
      {options.length === 0 ? (
        <p className="text-xs text-[#64748b]">
          Ticket cerrado — sin más transiciones.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={next}
              onChange={(e) => setNext(e.target.value as TicketStatus | '')}
              className={selectClass}
            >
              <option value="">Cambiar estado a…</option>
              {options.map((s) => (
                <option key={s} value={s}>
                  {TICKET_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
            <button
              onClick={apply}
              disabled={!next || busy}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#1f9e3f] hover:opacity-90 disabled:opacity-50"
            >
              {busy ? 'Aplicando…' : 'Aplicar'}
            </button>
          </div>

          {needsNotes && (
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={5000}
              placeholder="Notas de resolución (opcional)"
              className="w-full px-3 py-2 bg-[#334155] border border-[#475569] rounded-lg text-white placeholder-[#94a3b8] text-sm focus:outline-none focus:border-[#1f9e3f] resize-none"
            />
          )}

          {error && <p className="text-xs text-red-300">{error}</p>}
        </div>
      )}
    </TicketCard>
  );
};

const AdminTicketsView: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [filter, setFilter] = useState<TicketStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTickets(await TicketsService.listAll(filter || undefined));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <label className="text-[#94a3b8] text-sm">Filtrar:</label>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as TicketStatus | '')}
          className={selectClass}
        >
          <option value="">Todos</option>
          {TICKET_STATUSES.map((s) => (
            <option key={s} value={s}>
              {TICKET_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 rounded-full border-2 border-[#334155] border-t-[#1f9e3f] animate-spin" />
        </div>
      ) : error ? (
        <div className="p-4 rounded-lg bg-red-900/40 border border-red-500/50 text-red-300 text-sm">
          {error}
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-12 text-[#94a3b8]">
          No hay tickets{filter ? ' con ese estado' : ''}.
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <AdminTicketRow key={t.id} ticket={t} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTicketsView;
